// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 159808 $
//  Purpose     : templating sagas
// =============================================================================
// ---Third-party imports
import {
  call,
  cancel,
  put,
  take,
  takeEvery,
  takeLatest,
  all,
  spawn,
  putResolve,
  throttle,
  fork,
} from 'redux-saga/effects'
import {push, replace} from '../redux_router'
import now from 'performance-now'

// ---Framework imports
import {
  get,
  items,
  filterItems,
  fromPairs,
  mapItems,
  merge,
  size,
  has,
  sum,
  entries,
  values,
  flatten,
  isArray,
  isString,
  dict,
  map,
  uniq,
  isNil,
  sorted,
  keys,
  isObject,
  isFunction,
} from 'ldash'

import {select, delay, initializeDomain, loadGraphics} from 'uix/sagas'
import log from 'uix/logging'
import Api from 'uix/api'
import {uix, Actions, partProperties, Sel} from 'uix/registries'
import {decodeUrl, decodeDomain, decodeRequest, ensurePathPrefix} from 'uix/routing'
import {loadGraphicChunks, isGraphicChunkLoaded, Graphics} from 'uix/graphics'
import {loadUtilityChunks, isUtilityChunkLoaded} from 'uix/chunks'
import {normalize} from 'uix/normalize'
import {UnloadedDomainError} from 'uix/error'
import {hashObj, decodeSlimLink} from 'uix/utils'
import {applyTheme} from 'uix/styles/variables'

// ---Local imports
import {
  setFilterData,
  activateView,
  reloadPartFilters,
  reloadAllParts,
  loadStandalonePart,
  measureParts,
  setLoadedChunks,
} from './actions'

// ---Globals
let getFilterArgs = filter => {
  // only pop: type, name, part, lazy
  let {type, name, part, lazy, ...filterArgs} = filter
  return filterArgs
}
let getFilterVariant = filterArgs => mapItems(filterArgs, (k, v) => `${k}=${v}`).join('&')

export const getUserContext = root => ({
  language: get(root, 'meta.language'),
  groups: get(root, 'meta.user.groups', []),
})

let autoNormalize = fdata => {
  /* auto-normalize lists with uppercased OT_ID
   */
  let res = {}
  for (let [k, v] of entries(fdata)) {
    // XXX: disable uppercase form later
    if (isArray(v) && v.length > 0 && (has(v[0], 'ot_id') || has(v[0], 'OT_ID'))) {
      res[k] = normalize(v, {format: 'raw'})
    } else {
      res[k] = v
    }
  }
  return res
}

let getCachedFilter = (filterConfig, filterParams, userContext, cachedParts) => {
  /* match current filter configuration to filters from last runs
  example: use cache if neither dname nor id changes:  <filter cacheKeys="dname, id"/>
  */
  let usedCacheKeys = []
  let keymod = key => key
  if (isObject(filterConfig.cacheKeys)) {
    usedCacheKeys = filterConfig.cacheKeys.keys
    keymod = filterConfig.cacheKeys.keymod
  } else {
    usedCacheKeys = filterConfig.cacheKeys
  }
  // add some default cache invalidators
  usedCacheKeys = [...usedCacheKeys, 'part', 'name', 'type', 'language', 'groups']
  let used = new Set(usedCacheKeys)
  let calculateKey = keyData => filterItems(keymod(keyData), (k, v) => used.has(k))

  let searchCacheKey = calculateKey({
    ...filterConfig,
    ...filterParams,
    ...filterParams.query,
    ...userContext,
  })
  let reversedRuns = sorted(entries(cachedParts), ([k, v]) => k, true)
  let cacheKeys = [filterConfig]
  for (let [runId, parts] of reversedRuns) {
    for (let [partName, filters] of entries(parts)) {
      for (let [filterName, f] of entries(filters)) {
        let m = f.meta
        let cacheKey = calculateKey({...m.config, ...m.params, ...m.userContext})

        if (hashObj(searchCacheKey) === hashObj(cacheKey)) {
          log.debug('filter from cache: %o', filterConfig.name || filterConfig.type)
          return f
        }
      }
    }
  }
}

function* runFilter({filter, runId, ...filterParams}) {
  // DEBUG: delay request to see progressbar
  // yield delay(Math.random() * 2000)
  let {dname, cname, classname, tabname, ctxid, id, query} = filterParams

  let base = [dname, cname].filter(e => e).join('/')
  let {type, name, part} = filter

  // part-unbound filters store their data as 'Unbound'
  if (!part) {
    part = 'Unbound'
  }

  let filterArgs = getFilterArgs(filter)

  let path = `/${base}/api/filter/${type}`

  // XXX: int/float/bool/null typing disables "!" override form
  // maybe get rid of the "!" override possibility
  let overrideArgs = fromPairs(
    filterItems(filterArgs, (key, value) => isString(value) && value.slice(-1)[0] == '!').map(
      ([key, value]) => [key, value.slice(0, -1)]
    )
  )
  let defaultArgs = fromPairs(
    filterItems(filterArgs, (key, value) => !isString(value) || value.slice(-1)[0] != '!')
  )

  // unclear if tracking filterVariant is still important
  let filterVariant = getFilterVariant(filterArgs)

  let params = {}
  if (!isNil(ctxid) && ctxid !== '') {
    params.ctx = ctxid
  }
  if (!isNil(id) && id !== '') {
    params.id = id
  }
  params = {...params, ...defaultArgs, ...query, ...overrideArgs}

  // resolve slimlink situation
  // assume only ancestorAxisJson understands slimlink syntax
  if (type !== 'ancestorAxisJson') {
    params = dict(
      mapItems(params, (k, v) => [
        k,
        k == 'id' && decodeSlimLink(v).otid ? decodeSlimLink(v).otid : v,
      ])
    )
  }

  let userContext = yield select(getUserContext)
  // maybe intercept by cache
  if (filter.cacheKeys) {
    let cachedParts = yield select(root => root.data.newParts)
    let cachedFilterData = getCachedFilter(filter, filterParams, userContext, cachedParts)
    if (cachedFilterData) {
      yield put(Actions.progressStep({id: runId, step: filter2step(filter)}))
      return cachedFilterData
    }
  }

  // fetch from server
  let {data, error, authError} = yield call(
    Api.get,
    path,
    params,
    {},
    {silent: true, autoDomain: false}
  )
  yield put(Actions.progressStep({id: runId, step: filter2step(filter)}))

  // we can rerun the filter (part.reload()) with config + params
  let filterData = {
    meta: {
      part,
      type,
      name,
      variant: filterVariant,
      params: filterParams,
      config: filter,
      userContext,
    },
  }

  if (authError) {
    log.error('auth error: %o %o', type, authError)
  } else if (error) {
    log.error('error: %o %o', type, error)
    filterData.meta.error = error
  } else if (!data) {
    log.error(`no filter data: ${type}`)
  } else {
    let {meta, ...fdata} = data
    filterData.data = autoNormalize(fdata)
    filterData.meta.remote = meta
  }

  return filterData
}

let filter2step = filter => {
  let v = getFilterVariant(getFilterArgs(filter))
  return `${filter.type}/${v}`
}
function* showProgressDelayed({payload, meta}) {
  let progressDelay = payload.delay || 500
  const frameDelay = 50 // emit current duration each 50 ms
  const maxDuration = 20000 // 20 secs
  yield delay(progressDelay)

  let root = yield select()
  let timestamp = root.meta.progress.timestamp
  let isCurrent = p => payload.id == p.id && (p.status == 'running' || p.status == 'initialized')
  if (isCurrent(root.meta.progress)) {
    yield put(Actions.setProgressVisible())
    root = yield select()
    while (isCurrent(root.meta.progress) && now() - timestamp < maxDuration) {
      yield put(Actions.setProgressDuration(now() - timestamp))
      yield delay(frameDelay)
      root = yield select()
    }
  }
}

function* goMessage(requestParams, filterData, runId) {
  // send go message to server (track view changes for session analytics)
  let root = yield select()
  let pack = root.config.deploy['package']

  let {dname, cname, classname, tabname, ctxid, id, request} = requestParams
  let params = [dname, cname, classname, tabname, ctxid, id].filter(e => e)
  let qs = mapItems(requestParams.query, (k, v) => `${k}=${v}`).join('&')
  if (qs) {
    qs = '?' + qs
  }
  let url = `${root.config.apiurl}/${params.join('/')}${qs}`

  let fstats = []
  let durations = []
  for (let [partName, filters] of entries(filterData)) {
    for (let [filterName, f] of entries(filters)) {
      let duration = get(f, 'meta.remote.duration', 0) / 1000
      durations.push(duration)
      fstats.push(`${filterName}:${duration.toFixed(3)}`)
    }
  }

  yield Api.post(
    '/api/goMessage',
    {
      pack,
      dname,
      cname,
      classname,
      tabname,
      ctxid,
      id,
      url,
      collectTime: sum(durations),
      fstats: fstats.join(','),
      runId,
    },
    {},
    {silent: true}
  )
}

let getPartProp = (partType, propName, defaultValue) =>
  get(partProperties, partType, {})[propName] || defaultValue

function* checkRequiredChunks(parts) {
  /* import more code */

  let chunkNames = uniq(flatten(parts.map(p => getPartProp(p.type, 'getGraphics', []))))
  yield loadGraphics(chunkNames)

  let utilityChunkNames = uniq(
    flatten(parts.map(p => getPartProp(p.type, 'getChunkedUtils', []))).filter(
      name => !isUtilityChunkLoaded(name)
    )
  )

  if (utilityChunkNames.length > 0) {
    yield call(loadUtilityChunks, utilityChunkNames)
    yield put(setLoadedChunks(utilityChunkNames))
  }
}

let analyzeFilters = filters => {
  /* analyze filter configs and distribute in {eager, lazy, interval} slots
   */
  let _analyze = fs => ({
    eager: fs.filter(f => !f.lazy),
    lazy: fs.filter(f => f.lazy),
    // collect filters with uixInterval (see templateconfig) and run periodically
    interval: fs.filter(f => f.uixInterval),
  })

  // XXX: what about duplicate detection?
  let res = _analyze(filters)
  return res
}

const collectPartLocalFilters = partTypes => {
  // fetch filter cfgs from local part descriptions
  let withLocalFilters = partTypes.filter(ptype => getPartProp(ptype, 'filters'))
  let filters = []
  for (let ptype of withLocalFilters) {
    let cfgs = getPartProp(ptype, 'filters')
    if (isString(cfgs)) {
      // raw single string
      cfgs = [{type: cfgs}]
    } else if (!isArray(cfgs) && isObject(cfgs)) {
      cfgs = [cfgs]
    }
    // all remain strings are raw filter types
    cfgs = cfgs.map(cfg => (isString(cfg) ? {type: cfgs} : cfg))

    // set part type on filter
    cfgs = cfgs.map(cfg => ({...cfg, part: ptype}))
    for (let cfg of cfgs) {
      if (!cfg.type) {
        throw Error(`part ${ptype}: no @type given at local filter cfg ${keys(cfg)}`)
      }
      filters.push(cfg)
    }
  }
  return filters
}

let combineWithPartDescription = (filters, partTypes) => {
  return filters
}

function* _loadStandalonePart({payload}) {
  /* differs from semantic of filter loading during view transitions
  non-lazy filters will committed in batch
  lazy filters will be commited serially
  */
  let {name, dname, partcfg, runId, partStoreKey} = payload
  yield call(checkRequiredChunks, [partcfg])

  // XXX: partStoreKey override becomes ugly here
  let partLocalFilters = collectPartLocalFilters([partcfg.type])
  // resolve params
  let partFilters = [...partLocalFilters, ...partcfg.filters].map(f => {
    let {paramsFromPart, ...filterCfg} = f
    if (paramsFromPart) {
      if (isString(paramsFromPart)) {
        paramsFromPart = paramsFromPart.split(',').map(n => n.trim())
      }
      let partParams = dict(paramsFromPart.filter(n => has(partcfg, n)).map(n => [n, partcfg[n]]))
      return {...filterCfg, ...partParams, part: partStoreKey}
    } else {
      return {...filterCfg, part: partStoreKey}
    }
  })
  let filterParams = {dname}
  let filters = analyzeFilters(partFilters)

  let collected = yield all(
    filters.eager.map(f => call(runFilter, {filter: f, runId, ...filterParams}))
  )
  let currentRunId = yield select(Sel.runId)
  if (runId == currentRunId) {
    if (collected.length > 0) {
      yield call(setCollectedFilterData, collected, runId)
    }

    for (let f of filters.lazy) {
      yield spawn(runLazyFilter, f, filterParams, runId)
    }
  }
  // finalize
  yield put(Actions.setPartLoaded(name, partStoreKey, runId))
}

export function* runFilters({tcfg, runId, ...params}) {
  const classcfg = get(tcfg.classes, params.classname)
  if (!classcfg) {
    log.error(`no valid classname: ${params.classname}`)
    return
  }

  const tabcfg = get(classcfg.tabs, params.tabname)
  if (!tabcfg) {
    log.error(`no valid tabname: ${params.classname}/${params.tabname}`)
    return
  }

  const parts = [...classcfg.parts, ...tabcfg.parts]
  yield call(checkRequiredChunks, parts)

  const partTypes = uniq([...classcfg.parts.map(p => p.type), ...tabcfg.parts.map(p => p.type)])
  let partLocalFilters = collectPartLocalFilters(partTypes)
  let filters = analyzeFilters([...partLocalFilters, ...classcfg.filters, ...tabcfg.filters])

  yield put(
    Actions.initProgress({
      total: size(filters.eager),
      delay: 500,
      steps: filters.eager.map(f => filter2step(f)),
      id: runId,
      timestamp: now(),
    })
  )

  const collected = yield all(
    filters.eager.map(f => call(runFilter, {filter: f, runId, ...params}))
  )

  let currentRunId = yield select(Sel.runId)
  if (runId > currentRunId) {
    let filterData = yield call(setCollectedFilterData, collected, runId)
    yield fork(goMessage, params, filterData, runId)

    for (let f of filters.lazy) {
      yield spawn(runLazyFilter, f, params, runId)
    }

    if (process.env.UIX_ENV.includes('browser')) {
      for (let f of filters.interval) {
        yield spawn(runIntervalFilter, f, params, parseInt(f.uixInterval), runId)
      }
    }
  }

  yield put(Actions.finishProgress(runId))
}

function* setCollectedFilterData(collected, runId) {
  let filterData = {}
  collected.forEach(dat => {
    // multiple filters of the same type for one part need a custom name
    const {data, meta} = dat
    if (!filterData[meta.part]) {
      filterData[meta.part] = {}
    }
    filterData[meta.part][meta.name || meta.type] = dat
  })
  yield putResolve(setFilterData({filterData, runId}))
  return filterData
}

function* runLazyFilter(filter, filterParams, runId) {
  // run a single filter
  let {data, meta} = yield call(runFilter, {filter, runId, ...filterParams})
  let filterData = {[meta.part]: {[meta.name || meta.type]: {data, meta}}}
  // keeping runId stable here, replace last part/filter section
  yield put(setFilterData({filterData, runId}))
}

function* _reloadPartFilters({payload}) {
  /* reload all filters of a given part */
  let {part} = payload
  let runId = yield select(Sel.runId)

  let filters = yield select(root => root.data.parts[part] || {})
  if (size(filters) > 0) {
    let reloadCalls = mapItems(filters, (filterName, result) =>
      call(runFilter, {filter: result.meta.config, runId, ...result.meta.params})
    )
    const collected = yield all(reloadCalls)
    yield call(setCollectedFilterData, collected, runId)
  }
}

function* _reloadAllParts() {
  let {dname, classname, tabname} = yield select(root => root.meta.request)
  let tcfg = yield select(root => root.config.templating[dname])
  if (tcfg) {
    let tab = tcfg.classes[classname].tabs[tabname]
    for (let part of tab.parts) {
      yield put(Actions.reloadPartFilters(part.name || part.type))
    }
  }
}

function* runIntervalFilter(filter, filterParams, intervalSecs, runId) {
  // run a single filter periodically
  // initial wait, maybe configurable
  let interval = intervalSecs * 1000
  yield delay(interval)

  let _runId = yield select(Sel.runId)

  while (_runId == runId) {
    yield runLazyFilter(filter, filterParams, runId)

    // wait and check termination
    yield delay(interval)
    _runId = yield select(Sel.runId)
  }
}

function decodeGoParams(newRequest, oldRequest, templatingcfgs) {
  /* go with structured request params */
  if (has(newRequest, 'tab')) throw Error('use "tabname" instead of "tab"')
  if (has(newRequest, 'class')) throw Error('use "classname" instead of "class"')

  if (isNil(newRequest) || size(newRequest) == 0) {
    // reload current view
    var updateQuery = {}
    var request = {}
  } else {
    // fetch new request path params and seperate query params
    var {query, updateQuery, keepQuery, ...request} = newRequest

    if (!updateQuery && keepQuery) {
      updateQuery = {}
    }
  }

  // use merged updateQuery into oldQuery for new query string
  if (updateQuery) {
    if (has(updateQuery, 'id')) {
      // special case a updateQuery with id, move to request params
      request.id = updateQuery.id
      delete updateQuery.id
    }
    // use old + given values instead of given 'query'
    query = {...oldRequest.query, ...updateQuery}

    // delete query params with value "null"
    query = keys(query).reduce((r, e) => {
      if (query[e] !== null) r[e] = query[e]
      return r
    }, {})
  }
  // if no new dname given, reuse old
  let dname = request.dname || oldRequest.dname
  let templatingcfg = templatingcfgs[dname]
  if (!templatingcfg?.classes) {
    throw new UnloadedDomainError(dname)
  }

  let isDefault = {tabname: false, classname: false}
  const classname = request.classname || oldRequest.classname

  // choose default tab for class if no tab/ctxid/id param is given
  // only classname case: determine default tab
  if (!request.id && !request.ctxid && !request.tabname && request.classname) {
    const tabs = values(templatingcfg.classes[classname].tabs)
    let tabname = get(tabs.filter(t => t['default']), '0.name')
    if (!tabname) {
      log.error('route generation failed: got no default tab for classname %o', classname)
      return {} // XXX: throw?
    }
    request = {...request, tabname}
    isDefault.tabname = true
  }

  return {templatingcfg, request, query, isDefault}
}

function decodeGoUrl(url, domains, templatingcfgs, deploy) {
  /* go with url string */
  let {params, query} = decodeUrl(url)

  let dname = decodeDomain(params, domains, deploy.pathPrefix)

  let templatingcfg = templatingcfgs[dname]
  if (!templatingcfg) {
    throw new UnloadedDomainError(dname)
  }
  let {query: typedQuery, authNeeded, isDefault, ...request} = decodeRequest(params, query, {
    domains: domains,
    templating: {[dname]: templatingcfg},
    deploy,
  })

  return {templatingcfg, request, query: typedQuery, isDefault, dname}
}

function customizeRouting(request, query, oldRequest, templatingcfgs) {
  /* apply some custom route changing rules
    we are using the rules of the target domain here, but for the current (old) classname
    returns either a modified request params and/or modified query params
  */
  let merge = (prev, next) => ({...prev, ...(next || {})})
  let templatingcfg = templatingcfgs[request.dname]
  let tcfg_class = templatingcfg.classes[oldRequest.classname]
  let customizeRoutingClass = tcfg_class?.customizeRouting
  let customizeRoutingTab = tcfg_class?.tabs[oldRequest.tabname]?.customizeRouting
  // first apply tablevel rules
  let customized = customizeRoutingTab ? customizeRoutingTab(request, query, oldRequest) : {}
  request = merge(request, customized?.request)
  query = merge(query, customized?.query)
  // then apply classlevel rules
  if (customizeRoutingClass) {
    customized = customizeRoutingClass ? customizeRoutingClass(request, query, oldRequest) : {}
    request = merge(request, customized?.request)
    query = merge(query, customized?.query)
  }
  return {request, query}
}

export function decodeGo(urlOrRequest, oldRequest, domains, templatingcfgs, deploy) {
  if (isString(urlOrRequest)) {
    var {request, query, templatingcfg, isDefault, dname} = decodeGoUrl(
      urlOrRequest,
      domains,
      templatingcfgs,
      deploy
    )
  } else {
    let {odid} = urlOrRequest
    if (odid !== undefined && domains.odid2name[odid]) {
      urlOrRequest = {...urlOrRequest, dname: domains.odid2name[odid]}
    }
    var {request, query, templatingcfg, isDefault, dname} = decodeGoParams(
      urlOrRequest,
      oldRequest,
      templatingcfgs
    )
  }

  if (!dname) {
    dname = request.dname || oldRequest.dname
  }

  // pop query from old request
  let {query: oldQuery, ...filtered} = oldRequest

  // overwrite old request params with incoming
  let merged = {...filtered, ...request}

  let customized = customizeRouting(merged, query, oldRequest, templatingcfgs)
  merged = customized.request
  query = customized.query

  // give them names
  let {cname, classname, tabname, ctxid, id} = merged
  let namedParams = {dname, cname, classname, tabname, ctxid, id}

  let pathParams = [domains.name2prefix[dname], cname]
  if (!isNil(ctxid) || !isNil(id)) {
    pathParams = [...pathParams, classname, tabname, ctxid, id]
  } else if (!isDefault.classname) {
    // build reduced version (optional class/tabname)
    pathParams = [...pathParams, classname]
    if (!isDefault.tabname) {
      pathParams = [...pathParams, tabname, ctxid, id]
    }
  }

  pathParams = pathParams.filter(e => e !== '' && e !== null && e !== undefined)

  // bring them in order, filter and join them
  let pathname = '/' + pathParams.join('/')

  return {pathname, pathParams, namedParams, query, templatingcfg}
}

function* go(urlOrRequest, options, runId) {
  /* process go action
  some examples:
    go() => refresh
    go({updateQuery: {test: null}}) => delete param "test" from query params
    go({updateQuery: {test: 'hello'}}) => set query param "test" to "hello", further params will stay the same
    go({tabname: 'analytics'})} => go to analytics
    go('domain1/admin/db') => go to domain/class/tab
    etc. XXX
  */
  try {
    let offline = yield select(root => root.meta.state === 'API_OFFLINE')
    if (offline) return

    // fetch global config and current request state
    let oldRequest = yield select(Sel.request)
    let replacedQueryParams = yield select(root => root.meta.replacedQueryParams)
    // inject replaced query params
    oldRequest = {...oldRequest, query: {...oldRequest.query, ...replacedQueryParams}}

    let config = yield select(root => root.config)

    let decode = templatingcfgs =>
      decodeGo(urlOrRequest, oldRequest, config.domains, templatingcfgs, config.deploy)
    try {
      var decoded = decode(config.templating)
    } catch (e) {
      if (e.code == 'E_DOMAIN_UNLOADED') {
        yield call(initializeDomain, e.dname)
        let {templating} = yield select(root => root.config)
        var decoded = decode(templating)
      } else {
        throw e
      }
    }
    let {pathname, namedParams, templatingcfg, query} = decoded

    // run and wait
    yield call(runFilters, {
      ...namedParams,
      query,
      tcfg: templatingcfg,
      runId,
    })

    pathname = ensurePathPrefix(config.deploy.pathPrefix, pathname)

    // navigate with router
    let history = options.history || 'push'
    if (history === 'push') {
      yield put(push({pathname, query}, {runId}))
    } else if (history === 'replace') {
      yield put(replace({pathname, query}, {runId}))
    } else if (history === 'activate') {
      // only activate view, no history manipulation
      // we recover from a cache miss in ROUTER_POP/restoreView
      yield put(activateView(runId))
    }
  } catch (e) {
    log.exception('error during go %o %o %O', runId, urlOrRequest, e)
  }
}

function* runGo({payload, meta}) {
  let {urlOrRequest, options} = payload
  yield go(urlOrRequest, options, meta.runId)
}

function* persistSplitterThrottled({payload}) {
  /* dispatch to setUI */
  let {componentName, splitName, size} = payload
  yield delay(500)
  //~ log('%o %o', splitName, size)
  yield put(Actions.uiSetValue(componentName, {[splitName]: size}))
}

function* _measureParts({payload}) {
  let parts = document.getElementsByClassName('part')
  let nextPartMeasurements = dict(
    map(parts, p => [p.getAttribute('name'), p.getBoundingClientRect()])
  )
  let partMeasurements = yield select(root => root.debug.partMeasurements)
  yield put(
    Actions.setDebugData({
      partMeasurements: {...partMeasurements, [payload.reason]: nextPartMeasurements},
    })
  )
}

function* initTheme({payload, key}) {
  if (payload && key == 'uix_ui') {
    log.info('init theme')
    applyTheme(payload.byId['Global/theme'])
  }
}

export const sagas = {
  go: takeLatest('GO', runGo),
  showProgressDelayed:
    // makes testSpeedActionReload to slow
    process.env.UIX_ENV == 'express' ? null : takeLatest('INIT_PROGRESS', showProgressDelayed),
  persistSplitterThrottled: takeLatest('PERSIST_SPLITTER', persistSplitterThrottled),
  reloadPartFilters: takeEvery(reloadPartFilters, _reloadPartFilters),
  reloadAllParts: takeEvery(reloadAllParts, _reloadAllParts),
  loadStandalonePart: takeEvery(loadStandalonePart, _loadStandalonePart),
  measureParts: takeEvery(measureParts, _measureParts),
  initTheme: takeEvery('persist/REHYDRATE', initTheme),
}
